import {
  ensureSfSessionStart,
  resetSfSessionLimitKeys,
} from "./sfSessionLimits";

export type TokenSet = {
  /** Omitted when backend auth uses HttpOnly cookies only (`backendSessionCookie`). */
  access_token?: string;
  refresh_token?: string;
  id_token?: string;
  instance_url?: string;
  issued_at?: number;
  expires_in?: number;
};

export type AzureAuth = {
  tokenSet?: TokenSet;
  /**
   * When true, Azure access token for the Node backend is only in HttpOnly cookies (POST /api/auth/azure/token).
   * sessionStorage must not duplicate `access_token` or it can go stale after refresh.
   */
  backendSessionCookie?: boolean;
  /** Unix ms — access token / cookie validity end time (from MSAL `expiresOn` or `expires_in`). Client-side session check only. */
  backendCookieExpiresAt?: number;
  state?: string;
  codeVerifier?: string;
  lastAuthDomain?: string;
  user?: Record<string, unknown> | null;
  issuedAt?: number;
  account?: Record<string, unknown> | null;
};

export type SfAuth = {
  tokenSet?: TokenSet;
  state?: string;
  codeVerifier?: string;
  lastAuthDomain?: string;
  user?: Record<string, unknown> | null;
};

export type Auth = {
  sf?: SfAuth;
  azure?: AzureAuth;
};

const SF_NS = "SF_AUTH::v1";
const AZURE_NS = "AZURE_AUTH::v1";

function getKey(ns: string, key: string): string {
  return `${ns}::${key}`;
}

function safeParse<T>(raw: string | null): T | undefined {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

export const storage = {
  readAuth(): Auth | undefined {
    const sfAuth = this.readSfAuthSession();
    const azureAuth = this.readAzureAuthSession();
    return { sf: sfAuth, azure: azureAuth };
  },

  /** Auth status from storage only (no MSAL). Use in providers together with MSAL when available. */
  hasAzureSession(): boolean {
    const a = this.readAzureAuthSession();
    if (!a) return false;
    if (a.backendSessionCookie) {
      const exp = a.backendCookieExpiresAt;
      if (exp != null && Number.isFinite(exp) && Date.now() >= exp) return false;
      return true;
    }
    return Boolean(a.tokenSet?.access_token);
  },

  /** Auth status from storage only. */
  hasSfSession(): boolean {
    return Boolean(this.readSfAuthSession()?.tokenSet?.access_token);
  },
  /**
   * SF auth session — sessionStorage only (no auth tokens in localStorage).
   * @param _mirrorLocal deprecated, ignored; kept for call-site compatibility
   */
  saveSfAuthSession(data: SfAuth, _mirrorLocal: boolean = true): void {
    window.sessionStorage.setItem(getKey(SF_NS, "auth"), JSON.stringify(data));
    if (data?.tokenSet?.access_token) {
      ensureSfSessionStart();
    }
  },
  readSfAuthSession(): SfAuth | undefined {
    const sessionRaw = window.sessionStorage.getItem(getKey(SF_NS, "auth"));
    return safeParse<SfAuth>(sessionRaw);
  },
  clearSfAuthSession(): void {
    window.sessionStorage.removeItem(getKey(SF_NS, "auth"));
    resetSfSessionLimitKeys();
  },

  /**
   * Azure auth snapshot for this tab (sessionStorage only).
   * MSAL cache defaults to sessionStorage (see azureConfig); do not duplicate tokens in localStorage.
   * @param _mirrorLocal deprecated, ignored
   */
  saveAzureAuthSession(data: AzureAuth, _mirrorLocal: boolean = true): void {
    window.sessionStorage.setItem(
      getKey(AZURE_NS, "auth"),
      JSON.stringify(data),
    );
    try {
      window.localStorage.removeItem(getKey(AZURE_NS, "auth"));
    } catch {
      /* ignore */
    }
  },
  readAzureAuthSession(): AzureAuth | undefined {
    const sessionRaw = window.sessionStorage.getItem(getKey(AZURE_NS, "auth"));
    return safeParse<AzureAuth>(sessionRaw);
  },
  clearAzureAuthSession(): void {
    window.sessionStorage.removeItem(getKey(AZURE_NS, "auth"));
    window.localStorage.removeItem(getKey(AZURE_NS, "auth"));
  },

  /** Clear both SF and Azure auth sessions */
  clearAllAuthSessions(): void {
    storage.clearSfAuthSession();
    storage.clearAzureAuthSession();
  },

  /** Alias for clearAllAuthSessions (used by session error handlers) */
  clearAuthSession(): void {
    storage.clearAllAuthSessions();
  },

  saveTemp(data: {
    state: string;
    codeVerifier: string;
    authDomain: string;
  }): void {
    window.sessionStorage.setItem(getKey(SF_NS, "temp"), JSON.stringify(data));
  },
  readTemp():
    | { state: string; codeVerifier: string; authDomain: string }
    | undefined {
    return safeParse(window.sessionStorage.getItem(getKey(SF_NS, "temp")));
  },
  clearTemp(): void {
    window.sessionStorage.removeItem(getKey(SF_NS, "temp"));
  },
};
